import DOMPurify from "dompurify";

/**
 * Sanitize rich-text memo HTML for rendering / seeding a contentEditable.
 *
 * Memo content is authored via the formatting toolbar (styled <span>/<div>,
 * lists, links, basic marks) and stored as raw HTML. This is the single place
 * that decides what markup is allowed, so every render sink and every
 * `innerHTML =` seed must go through it (directly, or via <MemoContent/>).
 *
 * DOMPurify keeps the formatting the toolbar produces while stripping scripts,
 * on*= event handlers, and dangerous URL schemes (javascript:, data:, etc.).
 * `target` is explicitly allowed so toolbar-created links keep opening in a new
 * tab; the hook below forces rel="noopener noreferrer" on those to avoid
 * reverse-tabnabbing.
 *
 * On top of that, foreign box geometry is dropped — see stripForeignLayout.
 */

// Global hook (registered once on import). Guarded to only touch anchors that
// open in a new tab, so it is safe for any other DOMPurify usage.
let hookRegistered = false;
function ensureHook() {
    if (hookRegistered) return;
    DOMPurify.addHook("afterSanitizeAttributes", (node) => {
        if (node.tagName === "A" && node.getAttribute("target") === "_blank") {
            node.setAttribute("rel", "noopener noreferrer");
        }
    });
    hookRegistered = true;
}

// Pasting from another app drags that app's layout in with the text: copying a
// Gmail thread into a memo brings Gmail's scroll shell along, complete with
// `width: 100000px` wrappers, hard-coded `height: 892px` spacers and absolutely
// positioned filler divs. Sized for Gmail's own window, that geometry pushes
// memo text far outside the consultation card and pads it with hundreds of
// pixels of blank space. The formatting (color, font, alignment) is worth
// keeping; the box sizing of a layout the memo is not part of never is.
const FOREIGN_SIZE_PROPS = [
    "width", "min-width", "max-width",
    "height", "min-height", "max-height",
];
const OFFSET_PROPS = ["top", "right", "bottom", "left"];
// Elements whose inline size describes the content itself rather than the
// surrounding shell, so shrinking an image or a table column survives a paste.
const SELF_SIZED_TAGS = new Set([
    "IMG", "VIDEO", "CANVAS", "IFRAME", "TABLE", "TD", "TH", "COL", "COLGROUP",
]);

function stripForeignLayout(root) {
    root.querySelectorAll("[style]").forEach((el) => {
        const { style } = el;
        if (!SELF_SIZED_TAGS.has(el.tagName)) {
            FOREIGN_SIZE_PROPS.forEach((prop) => style.removeProperty(prop));
        }
        const position = style.getPropertyValue("position");
        if (position === "absolute" || position === "fixed") {
            // Out-of-flow inside a memo means overlapping whatever follows.
            style.removeProperty("position");
            OFFSET_PROPS.forEach((prop) => style.removeProperty(prop));
        }
        if (!style.length) el.removeAttribute("style");
    });
    return root;
}

export default function sanitizeMemoHtml(html) {
    if (!html) return "";
    ensureHook();
    const clean = DOMPurify.sanitize(String(html), {
        ADD_ATTR: ["target"],
        RETURN_DOM: true,
    });
    return stripForeignLayout(clean).innerHTML;
}
